import React, { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, UserIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const STEPS = {
  ASK: 'ask',
  ROLE: 'role',
  CONFIRM: 'confirm',
  PICK: 'pick'
};

const AdjusterConfirmModal = ({ isOpen, claim, onSave, onSkip, saving = false }) => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const [step, setStep] = useState(STEPS.ASK);
  const [role, setRole] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [counterpartId, setCounterpartId] = useState('');
  const [search, setSearch] = useState('');
  const [displayClaim, setDisplayClaim] = useState(claim);

  useEffect(() => {
    if (!isOpen) return;
    setStep(STEPS.ASK);
    setRole(null);
    setCounterpartId('');
    setSearch('');
    setDisplayClaim(claim);
  }, [isOpen, claim?.id]);

  useEffect(() => {
    if (!isOpen || !claim?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await makeAuthenticatedRequest(`/claims/${claim.id}`);
        if (!cancelled && data?.success && data.data) {
          setDisplayClaim({ ...claim, ...data.data });
        }
      } catch (e) {
        // keep the claim we were given
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, claim?.id, makeAuthenticatedRequest]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await makeAuthenticatedRequest('/customers');
        if (!cancelled && data?.success) {
          setCustomers(data.data || []);
        }
      } catch (e) {
        if (!cancelled) setCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, makeAuthenticatedRequest]);

  const otherCustomers = useMemo(() => {
    return (customers || []).filter((c) => String(c.id) !== String(customer?.id));
  }, [customers, customer?.id]);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return otherCustomers;
    return otherCustomers.filter((c) =>
      (c.name || '').toLowerCase().includes(term) ||
      (c.email || '').toLowerCase().includes(term)
    );
  }, [otherCustomers, search]);

  if (!isOpen || !claim) return null;

  const activeClaim = (displayClaim && claim && String(displayClaim.id) === String(claim.id))
    ? displayClaim
    : claim;
  const claimLabel = activeClaim?.claim_number ? `Claim #${activeClaim.claim_number}` : 'this claim';
  const claimantName = activeClaim?.claimant_name
    || [activeClaim?.claimant_first_name, activeClaim?.claimant_last_name].filter(Boolean).join(' ')
    || '';
  const handlerName = activeClaim?.contact_claims_handler_name || null;
  const assistantName = activeClaim?.adjusters_assistant_name || null;
  const handlerId = activeClaim?.contact_claims_handler_id || null;
  const assistantId = activeClaim?.adjusters_assistant_id || null;
  const counterpartOnFile = role === 'handler'
    ? { id: assistantId, name: assistantName, label: "adjuster's assistant" }
    : { id: handlerId, name: handlerName, label: 'claims handler' };

  const goPickCounterpart = () => {
    setCounterpartId(counterpartOnFile.id ? String(counterpartOnFile.id) : '');
    setSearch('');
    setStep(STEPS.PICK);
  };

  const handleChooseRole = (nextRole) => {
    setRole(nextRole);
    const hasCounterpart = nextRole === 'handler' ? assistantId : handlerId;
    if (hasCounterpart) {
      setStep(STEPS.CONFIRM);
    } else {
      setCounterpartId('');
      setSearch('');
      setStep(STEPS.PICK);
    }
  };

  const handleSave = (idOverride) => {
    const counterpart = idOverride !== undefined ? idOverride : counterpartId;
    if (!role) return;
    onSave({ role, counterpartId: counterpart || null });
  };

  const primaryBtn = 'w-full px-4 py-3 rounded-xl text-base font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
  const secondaryBtn = 'w-full px-4 py-3 rounded-xl text-base font-semibold bg-gray-100 text-gray-800 hover:bg-gray-200';
  const cardBtn = 'w-full text-left px-4 py-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-1">
          {claimLabel}
        </p>
        {claimantName ? (
          <p className="text-sm text-gray-500 mb-4">{claimantName}</p>
        ) : (
          <div className="mb-4" />
        )}

        {step === STEPS.ASK && (
          <>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Are you working this file?
            </h3>
            <p className="text-gray-600 mb-6">
              You&apos;re not listed as the claims handler, assistant, nurse case manager, or field
              case manager. Are you the claims handler or adjuster&apos;s assistant?
            </p>
            <div className="space-y-3">
              <button type="button" className={primaryBtn} onClick={() => setStep(STEPS.ROLE)}>
                Yes, that&apos;s me
              </button>
              <button type="button" className={secondaryBtn} onClick={onSkip}>
                No, I&apos;m just looking
              </button>
            </div>
          </>
        )}

        {step === STEPS.ROLE && (
          <>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Which one are you?</h3>
            <p className="text-gray-600 mb-6">We&apos;ll put you on the claim in that role.</p>
            <div className="space-y-3">
              <button type="button" className={cardBtn} onClick={() => handleChooseRole('handler')}>
                <div className="flex items-center">
                  <BriefcaseIcon className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Claims Handler</p>
                    <p className="text-sm text-gray-500">Contact / claims adjuster on the file</p>
                  </div>
                </div>
              </button>
              <button type="button" className={cardBtn} onClick={() => handleChooseRole('assistant')}>
                <div className="flex items-center">
                  <UserIcon className="h-6 w-6 text-indigo-600 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Adjuster&apos;s Assistant</p>
                    <p className="text-sm text-gray-500">Assistant on the file</p>
                  </div>
                </div>
              </button>
              <button type="button" className={secondaryBtn} onClick={() => setStep(STEPS.ASK)}>
                Back
              </button>
            </div>
          </>
        )}

        {step === STEPS.CONFIRM && (
          <>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Is the {counterpartOnFile.label} still correct?
            </h3>
            <p className="text-gray-600 mb-6">
              <span className="font-semibold text-gray-900">
                {counterpartOnFile.name || 'Someone'}
              </span>
              {' '}is listed as the {counterpartOnFile.label} on {claimLabel}.
            </p>
            <div className="space-y-3">
              <button
                type="button"
                className={primaryBtn}
                disabled={saving}
                onClick={() => handleSave(counterpartOnFile.id)}
              >
                {saving ? 'Saving…' : 'Yes, that\'s correct'}
              </button>
              <button type="button" className={secondaryBtn} onClick={goPickCounterpart}>
                No, someone else
              </button>
              <button type="button" className={secondaryBtn} onClick={() => setStep(STEPS.ROLE)}>
                Back
              </button>
            </div>
          </>
        )}

        {step === STEPS.PICK && (
          <>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Who is the {counterpartOnFile.label}?
            </h3>
            <p className="text-gray-600 mb-4">
              {counterpartOnFile.id
                ? `Pick the correct ${counterpartOnFile.label} for this file.`
                : `There isn't one on file yet. Pick the ${counterpartOnFile.label}.`}
            </p>
            <div className="relative mb-3">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg mb-4">
              {filteredCustomers.length === 0 ? (
                <p className="px-3 py-4 text-sm text-gray-500 text-center">
                  No other contacts on this account
                </p>
              ) : (
                filteredCustomers.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCounterpartId(String(c.id))}
                    className={`w-full text-left px-3 py-2.5 border-b border-gray-100 last:border-b-0 ${
                      String(counterpartId) === String(c.id)
                        ? 'bg-blue-50 text-blue-900'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <p className="text-sm font-medium">{c.name}</p>
                    {c.email ? <p className="text-xs text-gray-500">{c.email}</p> : null}
                  </button>
                ))
              )}
            </div>
            <div className="space-y-3">
              <button
                type="button"
                className={primaryBtn}
                disabled={saving || !counterpartId}
                onClick={() => handleSave(counterpartId)}
              >
                {saving ? 'Saving…' : 'Save and continue'}
              </button>
              {!counterpartOnFile.id && otherCustomers.length === 0 && (
                <button
                  type="button"
                  className={secondaryBtn}
                  disabled={saving}
                  onClick={() => handleSave(null)}
                >
                  Continue without one
                </button>
              )}
              <button
                type="button"
                className={secondaryBtn}
                onClick={() => setStep(counterpartOnFile.id ? STEPS.CONFIRM : STEPS.ROLE)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdjusterConfirmModal;
